import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LoadingContainer } from './ui/Spinner';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/formatting';
import { useBankrollMismatch } from '../contexts/BankrollMismatchContext';

function BankrollStorage({ className = '' }) {
    const [storageLocations, setStorageLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        storage_type: 'OTHER',
        amount: '',
        notes: ''
    });
    const { triggerMismatchCheck } = useBankrollMismatch();

    const storageTypes = [
        { value: 'BANK_ACCOUNT', label: 'Bank Account' },
        { value: 'SAFE', label: 'Safe' },
        { value: 'CASH', label: 'Cash' },
        { value: 'ONLINE', label: 'Online' },
        { value: 'OTHER', label: 'Other' }
    ];

    const fetchStorageLocations = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/bankroll-storage/');
            setStorageLocations(response.data || []);
        } catch (err) {
            setError('Failed to fetch storage locations.');
            console.error('Error fetching storage locations:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStorageLocations();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await axios.patch(`/api/bankroll-storage/${editingId}/`, formData);
            } else {
                await axios.post('/api/bankroll-storage/', formData);
            }
            setFormData({ name: '', storage_type: 'OTHER', amount: '', notes: '' });
            setShowForm(false);
            setEditingId(null);
            fetchStorageLocations();
            triggerMismatchCheck(); // Trigger mismatch check after updating storage
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save storage location.');
        }
    };

    const handleEdit = (location) => {
        setFormData({
            name: location.name,
            storage_type: location.storage_type,
            amount: location.amount,
            notes: location.notes || ''
        });
        setEditingId(location.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this storage location?')) {
                    try {
            await axios.delete(`/api/bankroll-storage/${id}/`);
            fetchStorageLocations();
            triggerMismatchCheck(); // Trigger mismatch check after deleting storage
            } catch (err) {
                setError('Failed to delete storage location.');
            }
        }
    };

    const handleCancel = () => {
        setFormData({ name: '', storage_type: 'OTHER', amount: '', notes: '' });
        setShowForm(false);
        setEditingId(null);
    };

    const totalAmount = storageLocations.reduce((sum, location) => sum + parseFloat(location.amount || 0), 0);

    return (
        <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm ${className}`}>
            <div className="p-3 sm:p-6">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <div>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Bankroll Storage</h2>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Track where your bankroll is stored</p>
                    </div>
                    <button
                        onClick={() => setShowForm(true)}
                        className="p-2 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                        <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                </div>

                {/* Total Amount */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Stored Amount</div>
                    <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{formatCurrency(totalAmount)}</div>
                </div>

                {/* Add/Edit Form */}
                {showForm && (
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                            {editingId ? 'Edit Storage Location' : 'Add Storage Location'}
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 
                                                 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                                                 focus:border-primary-500 dark:focus:border-primary-400 
                                                 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                                        placeholder="e.g., Chase Checking, Home Safe"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Type *
                                    </label>
                                    <select
                                        value={formData.storage_type}
                                        onChange={(e) => setFormData({ ...formData, storage_type: e.target.value })}
                                        required
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 
                                                 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                                                 focus:border-primary-500 dark:focus:border-primary-400 
                                                 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                                    >
                                        {storageTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Amount *
                                </label>
                                <input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    required
                                    min="0"
                                    step="0.01"
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 
                                             bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                                             focus:border-primary-500 dark:focus:border-primary-400 
                                             focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                                    placeholder="0.00"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows="3"
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 
                                             bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                                             focus:border-primary-500 dark:focus:border-primary-400 
                                             focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                                    placeholder="Additional notes about this storage location"
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                >
                                    {editingId ? 'Update' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
                        {error}
                    </div>
                )}

                {/* Storage Locations List */}
                {loading ? (
                    <LoadingContainer />
                ) : storageLocations.length === 0 ? (
                    <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                        No storage locations found. Add your first location to get started.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {storageLocations.map((location) => (
                            <div key={location.id} className="flex justify-between items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3">
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {location.name}
                                        </div>
                                        <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                                            {location.storage_type_display}
                                        </span>
                                    </div>
                                    {location.notes && (
                                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            {location.notes}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="text-right">
                                        <div className="font-semibold text-gray-900 dark:text-white">
                                            {formatCurrency(location.amount)}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {new Date(location.updated_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleEdit(location)}
                                            className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(location.id)}
                                            className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default BankrollStorage; 